import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { formatDuration } from "../../lib/format";
import { normalizeImageUrl } from "../../lib/imageUrl";
import PublicNav from "../../components/PublicNav";
import Footer from "../../components/Footer";
import NewsletterCTA from "../../components/NewsletterCTA";
import Seo, { LOCAL_BUSINESS_SCHEMA } from "../../components/Seo";

// Subtle inline SVG placeholder (no network request) shown only while
// settings load OR when admin hasn't uploaded an image yet.
const PLACEHOLDER = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><rect width='4' height='5' fill='%23F0E8D8'/></svg>";

const TESTIMONIALS = [
  { stars: 5, quote: "The city became our live game board. We felt like real detectives on a high-stakes mission.", name: "Jordan Reed", role: "Resident · Case #302" },
  { stars: 5, quote: "Cinematic storytelling turned the Edge District into a crime scene. Perfectly paced for a Saturday afternoon adventure.", name: "Sophia Martinez", role: "Visiting · Case #518" },
  { stars: 5, quote: "Reserved for our anniversary. Clues, puzzles, hand-picked spots — pure St. Pete magic.", name: "Daniel & Avery", role: "Returning investigators" },
];

export default function Home() {
  const [tours, setTours] = useState([]);
  const [hs, setHs] = useState(null);
  useEffect(() => {
    api.get("/tours?active_only=true").then(r => setTours(r.data)).catch(() => {});
    api.get("/public/home-settings").then(r => {
      setHs(r.data);
      if (r.data?.seo_title) document.title = r.data.seo_title;
      if (r.data?.seo_description) {
        let m = document.querySelector('meta[name="description"]');
        if (!m) {
          m = document.createElement("meta");
          m.setAttribute("name", "description");
          document.head.appendChild(m);
        }
        m.setAttribute("content", r.data.seo_description);
      }
    }).catch(() => {});
  }, []);

  // Show a real image ONLY if settings have loaded AND the admin set one.
  // Otherwise show the lightweight cream placeholder — no Unsplash hit.
  const heroImg = normalizeImageUrl(hs?.home_hero_image) || PLACEHOLDER;
  const eyebrow = hs?.home_eyebrow || "An interactive mystery adventure";
  const head1 = hs?.home_headline_1 || "Live a real mystery in";
  const head2 = hs?.home_headline_2 || "St. Petersburg.";
  const subhead = hs?.home_subhead || "Follow clues. Solve puzzles. Explore hidden places. An immersive adventure through the city built for your team.";
  const ctaPrimary = hs?.home_cta_primary_label || "Book your Mystery";
  const ctaSecondary = hs?.home_cta_secondary_label || "See all dates";

  return (
    <div className="public-shell">
      <Seo
        url="https://mysteryserved.com/"
        schema={LOCAL_BUSINESS_SCHEMA}
      />
      <PublicNav />

      {/* HERO — split editorial: cream copy left, sunlit photo right */}
      <section className="bg-ms-cream pt-28 md:pt-32 pb-16 md:pb-24" data-testid="hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center min-h-[78vh]">
          {/* LEFT — text on cream */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="label-eyebrow text-ms-brass animate-fade-up">{eyebrow}</p>
            <h1 className="serif text-ms-ink mt-6 text-5xl md:text-6xl lg:text-7xl leading-[1.02] animate-fade-up" style={{animationDelay:'120ms'}}>
              {head1}
              <br />
              <em className="text-ms-brass italic">{head2}</em>
            </h1>
            <div className="mt-7 w-16 h-[2px] bg-ms-brass animate-fade-up" style={{animationDelay:'200ms'}} />
            <p className="mt-7 max-w-md text-ms-stone text-base md:text-lg leading-[1.7] animate-fade-up" style={{animationDelay:'280ms'}}>
              {subhead}
            </p>
            <div className="mt-10 flex flex-wrap gap-3 animate-fade-up" style={{animationDelay:'360ms'}}>
              <Link to="/tours" className="btn-primary !py-3.5 !px-7" data-testid="hero-tours-btn">
                {ctaPrimary}
              </Link>
              <Link to="/dates" className="btn-secondary !py-3.5 !px-7" data-testid="hero-book-btn">
                {ctaSecondary}
              </Link>
            </div>
          </div>

          {/* RIGHT — sunlit photograph */}
          <div className="lg:col-span-7 order-1 lg:order-2 animate-fade-up" style={{animationDelay:'180ms'}}>
            <div className="relative aspect-[5/6] md:aspect-[6/5] lg:aspect-[5/6] overflow-hidden rounded-sm shadow-[0_30px_80px_-30px_rgba(184,117,63,0.35)] bg-ms-cream">
              <img src={heroImg} alt="" loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tl from-ms-cream/30 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* INTRO — editorial two-column on cream */}
      <section className="bg-ms-cream py-24 md:py-36" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <p className="label-eyebrow text-ms-brass">{hs?.intro_eyebrow || "An invitation"}</p>
            <h2 className="serif text-ms-ink text-4xl md:text-5xl mt-5 leading-[1.05]">
              {hs?.intro_headline || "A live mystery game across St. Petersburg, designed by a local artist."}
            </h2>
            <p className="mt-6 text-ms-stone text-base leading-[1.7] max-w-md whitespace-pre-line">
              {hs?.intro_body || "Mystery Served blends a custom story app with real-world puzzles to create an adventure unlike anything else."}
            </p>
            <Link to="/tours" className="mt-8 inline-flex items-center gap-2 text-ms-ink hover:text-ms-brass label-eyebrow text-xs border-b border-ms-brass pb-1" data-testid="intro-cta">
              {hs?.intro_cta_label || "Pick your mystery"} →
            </Link>
          </div>
          <div className="md:col-span-7 order-1 md:order-2">
            {(hs?.intro_image_main || hs?.intro_image_top || hs?.intro_image_bottom) ? (
              <div className="grid grid-cols-12 gap-3">
                {hs?.intro_image_main && <img src={hs.intro_image_main} alt="" loading="lazy" decoding="async" className="col-span-8 aspect-[4/5] object-cover rounded-sm bg-ms-cream" />}
                {!hs?.intro_image_main && <div className="col-span-8 aspect-[4/5] rounded-sm bg-ms-cream" />}
                <div className="col-span-4 flex flex-col gap-3">
                  {hs?.intro_image_top ? (
                    <img src={hs.intro_image_top} alt="" loading="lazy" decoding="async" className="aspect-square object-cover rounded-sm bg-ms-cream" />
                  ) : (
                    <div className="aspect-square rounded-sm bg-ms-cream" />
                  )}
                  {hs?.intro_image_bottom ? (
                    <img src={hs.intro_image_bottom} alt="" loading="lazy" decoding="async" className="aspect-square object-cover rounded-sm bg-ms-cream" />
                  ) : (
                    <div className="aspect-square rounded-sm bg-ms-cream" />
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — numbered editorial */}
      <section className="bg-parchment py-24 md:py-32" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="label-eyebrow text-ms-brass">{hs?.steps_eyebrow || "How it works"}</p>
          <h2 className="serif text-ms-ink text-4xl md:text-5xl mt-4 max-w-2xl leading-[1.05]">
            {hs?.steps_headline || "A live mystery experience your team uncovers together."}
          </h2>
          <div className="hairline mt-12" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 mt-12">
            {[
              { n: "01", t: hs?.step1_title, d: hs?.step1_body },
              { n: "02", t: hs?.step2_title, d: hs?.step2_body },
              { n: "03", t: hs?.step3_title, d: hs?.step3_body },
            ].map((s, i) => (
              <div key={i} className="md:pr-8" data-testid={`step-${i}`}>
                <p className="serif text-ms-brass text-5xl">{s.n}</p>
                <h3 className="serif text-ms-ink text-2xl mt-4">{s.t}</h3>
                <p className="mt-3 text-ms-stone text-sm leading-[1.7] whitespace-pre-line">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED TOURS — photo-forward */}
      <section className="bg-ms-cream py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="label-eyebrow text-ms-brass">{hs?.featured_eyebrow || "Pick your mystery"}</p>
              <h2 className="serif text-ms-ink text-4xl md:text-5xl mt-4">{hs?.featured_headline || "St. Pete Adventures"}</h2>
            </div>
            <Link to="/tours" className="hidden md:block label-eyebrow text-xs text-ms-ink hover:text-ms-brass border-b border-ms-brass pb-1" data-testid="see-all-tours">See all →</Link>
          </div>
          {tours.length === 0 ? (
            <div className="bg-ms-ivory border border-ms-edge rounded-sm p-16 text-center">
              <p className="serif text-3xl text-ms-ink">The cast is rehearsing.</p>
              <p className="mt-3 text-ms-stone text-sm">New tours will appear soon. Follow along below.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
              {tours.slice(0, 3).map((t) => (
                <Link key={t.id} to={`/tour/${t.slug}`} className="group block" data-testid={`tour-card-${t.slug}`}>
                  <div className="relative aspect-[4/5] overflow-hidden bg-ms-clay rounded-sm">
                    {t.hero_image ? (
                      <img src={normalizeImageUrl(t.hero_image)} alt={t.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                    ) : null}
                    <div className="absolute top-4 left-4 bg-ms-ink text-ms-cream px-3 py-1.5 rounded-full label-eyebrow text-[10px] shadow-lg" data-testid={`tour-card-price-${t.slug}`}>
                      From ${t.base_price}
                    </div>
                  </div>
                  <div className="mt-6">
                    <p className="label-eyebrow text-ms-brass text-[10px]">{t.difficulty || "Easy"} · {formatDuration(t)}</p>
                    <h3 className="serif text-3xl text-ms-ink mt-2 group-hover:text-ms-brass transition-colors">{t.title}</h3>
                    <p className="text-ms-stone text-sm mt-3 leading-[1.7] line-clamp-2">{t.short_description}</p>
                    <div className="mt-5 flex items-baseline justify-between gap-3">
                      <p className="text-ms-ink">
                        <span className="serif text-2xl text-ms-brass">${t.base_price}</span>
                        <span className="text-ms-stone text-xs ml-2">per guest</span>
                      </p>
                      <span className="label-eyebrow text-[10px] text-ms-ink group-hover:text-ms-brass border-b border-ms-brass pb-0.5 transition-colors">View tour →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* TESTIMONIALS — hidden by default; toggle in admin "Marketing" section */}
      {hs?.show_testimonials && (
      <section className="bg-parchment py-24 md:py-32" data-testid="testimonials">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="label-eyebrow text-ms-brass">Field intelligence reports</p>
          <h2 className="serif text-ms-ink text-4xl md:text-5xl mt-4 max-w-2xl">From the case files.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-14">
            {TESTIMONIALS.map((t, i) => (
              <figure key={i} className="bg-ms-ivory border border-ms-edge rounded-sm p-8 md:p-10" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 text-ms-brass">{Array.from({length:t.stars}).map((_,j)=>(<span key={j}>★</span>))}</div>
                <blockquote className="serif italic text-ms-ink text-xl md:text-2xl mt-5 leading-snug">"{t.quote}"</blockquote>
                <figcaption className="mt-6 pt-6 border-t border-ms-edge">
                  <p className="text-ms-ink text-sm">{t.name}</p>
                  <p className="text-ms-stone text-xs mt-1">{t.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* CTA — light airy editorial */}
      <section className="bg-ms-cream py-24 md:py-32" data-testid="cta-band">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-7 order-2 md:order-1">
            <p className="label-eyebrow text-ms-brass">{hs?.cta_eyebrow || "Ready to book your mystery?"}</p>
            <h2 className="serif text-ms-ink text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.02]">{hs?.cta_headline || "Pick your date and team. Your mystery begins now."}</h2>
            <p className="mt-6 text-ms-stone text-base leading-[1.75] max-w-lg whitespace-pre-line">{hs?.cta_body || "Reserve your spot and choose your team."}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/tours" className="btn-primary-dark !py-3.5 !px-7" data-testid="cta-band-btn">
                {ctaPrimary}
              </Link>
              <Link to="/dates" className="btn-secondary !py-3.5 !px-7">
                {ctaSecondary}
              </Link>
            </div>
          </div>
          <div className="md:col-span-5 order-1 md:order-2">
            {hs?.cta_image ? (
              <div className="aspect-[4/5] overflow-hidden rounded-sm bg-ms-cream">
                <img src={normalizeImageUrl(hs.cta_image)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <NewsletterCTA />
      <Footer />
    </div>
  );
}
